// src/utils/date.ts

import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * Define os formatos padrão do Brasil que queremos suportar.
 */
const BR_FORMATS = {
  /** Apenas data: 11/11/2025 */
  date: "dd/MM/yyyy",
  /** Data e hora: 11/11/2025 às 11:09 */
  datetime: "dd/MM/yyyy 'às' HH:mm",
  /** Data, hora e segundos: 11/11/2025 às 11:09:03 */
  datetimeWithSeconds: "dd/MM/yyyy 'às' HH:mm:ss",
  /** Apenas hora: 11:09 */
  time: "HH:mm",
  /** Apenas hora com segundos: 11:09:03 */
  timeWithSeconds: "HH:mm:ss",
};

type BrFormatType = keyof typeof BR_FORMATS;
type DateInput = Date | string | number | null | undefined;

interface FormatToBrOptions {
  /**
   * O tipo de formato desejado.
   * @default 'date'
   */
  formatType?: BrFormatType;
  /**
   * String para exibir se a data for nula, indefinida ou inválida.
   * @default '---'
   */
  fallback?: string;
}

/**
 * Formata uma data de entrada (Date, ISO string ou timestamp)
 * para um formato padrão do Brasil (pt-BR).
 *
 * @param dateInput A data a ser formatada.
 * @param options Opções de formatação (tipo de formato e fallback).
 * @returns A data formatada como string em pt-BR.
 */
export const formatToBr = (dateInput: DateInput, options: FormatToBrOptions = {}): string => {
  const { formatType = "date", fallback = "---" } = options;

  // 1. Lida com datas nulas ou indefinidas
  if (!dateInput) {
    return fallback;
  }

  // 2. Converte a entrada para um objeto Date
  //    parseISO é mais robusto para strings de data/hora
  const date = typeof dateInput === "string" ? parseISO(dateInput) : new Date(dateInput);

  // 3. Valida a data
  if (!isValid(date)) {
    console.warn(`[formatToBr] Recebeu uma data inválida: ${dateInput}`);
    // Retorna um fallback mais específico se a entrada não era nula
    return options.fallback ?? "Data inválida";
  }

  // 4. Obtém o string do formato BR correto
  const formatString = BR_FORMATS[formatType];

  // 5. Formata a data usando date-fns com o locale pt-BR
  try {
    return format(date, formatString, { locale: ptBR });
  } catch (error) {
    console.error(`[formatToBr] Erro ao formatar data: ${error}`);
    return fallback;
  }
};
